import React, { useCallback, useEffect, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  actionCacheMarketId,
  actionGetMarketRequest,
} from '../../store/Markets/MarketsAction';
import { actionSetSearchOverlayVisible } from '../../store/Search/SearchAction';
import { useIsMounted } from '../../utils/useIsMounted';
import { useViewport } from '../../utils/useViewport';
import FocusTrap from 'focus-trap-react';
import clsx from 'clsx';
import { NavBarClose } from '../../components/spoon/NavBar/NavBarClose';
import PopularSearches from './PopularSearches';
import {
  useCuisineAndRestaurantSearch,
  DEBOUNCE_MILLISECONDS,
  SEARCH_TRACK_MAX_LENGTH,
  goToSearchPage,
} from '../../components/spoon/Search/useCuisineAndRestaurantSearch';
import SearchBar from '../../components/spoon/Search/SearchBar';
import { Autocomplete } from '../../components/spoon/Search/Autocomplete';
import {
  SEARCH_ACTION_BUTTON_TEXT,
  SEARCH_INPUT_PLACEHOLDER,
} from '../../assets/copy';
import { debounce } from 'lodash';
import { trackSiteSearch } from '../../utils/useOneTag';
import { Button } from '../../components/spoon/Button';
import { handleSearchSelect } from '../../components/search/helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    width: '100vw',
    height: '100dvh',
    background: theme.palette.background.white,
    top: 0,
    left: 0,
    zIndex: 20,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  navbar: {
    borderBottom: `1px solid ${theme.palette.beacon.warmGray}`,
  },
  searchBar: {
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(2),
    '& .MuiFormControl-root, & #overlay-search-popup': {
      width: '100%',
    },
    '& #overlay-search-popup': {
      position: 'static',
      zIndex: 0,
      boxShadow: 'none',
    },
  },
  popularSearchContainer: {
    margin: theme.spacing(0, 2),
    borderTop: `1px solid ${theme.palette.beacon.warmGray}`,
    padding: theme.spacing(2, 0),
  },
  content: {
    maxWidth: theme.breakpoints.values.lg,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('lg')]: {
      width: theme.breakpoints.values.lg,
      alignSelf: 'center',
    },
  },
  listbox: {
    maxHeight: 'none',
  },
  searchButtonContainer: {
    margin: theme.spacing(0, 2, 3),
    borderTop: `1px solid ${theme.palette.beacon.warmGray}`,
    paddingTop: theme.spacing(2),
    '& button': {
      width: '-webkit-fill-available',
    },
  },
  spacer: {
    flexGrow: 1,
  },
}));

const widthBreakpoint = 960;

const OverlaySearchBar = ({
  getRootProps,
  getInputProps,
  getClearProps,
  getListboxProps,
  getOptionProps,
  groupedOptions,
}) => {
  const classes = useStyles();
  const inputProps = getInputProps();
  return (
    <div {...getRootProps()} className={classes.searchBar}>
      <SearchBar
        getInputProps={getInputProps}
        getClearProps={getClearProps}
        placeholder={SEARCH_INPUT_PLACEHOLDER}
        autoFocus
      />
      {inputProps.value.length > 0 && (
        <Autocomplete
          groupedOptions={groupedOptions}
          getListboxProps={getListboxProps}
          getOptionProps={getOptionProps}
          getInputProps={getInputProps}
          classes={{ listbox: classes.listbox }}
        />
      )}
    </div>
  );
};

const SearchOverlay = () => {
  const { width } = useViewport();
  const isMobile = useMemo(() => width < widthBreakpoint, [width]);
  const dispatch = useDispatch();
  const history = useHistory();
  const isMounted = useIsMounted();
  const { cachedMarketId, market } = useSelector((state) => state.markets);
  const { overlayVisible } = useSelector((state) => state.search);
  const classes = useStyles();
  const {
    getRootProps,
    getInputProps,
    getClearProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useCuisineAndRestaurantSearch({ id: 'overlay-search' });
  const inputProps = getInputProps();

  useEffect(() => {
    return history.listen(() => {
      if (overlayVisible) {
        dispatch(actionSetSearchOverlayVisible(false));
      }
    });
  }, [dispatch, history, overlayVisible]);

  const closeOverlay = useCallback(() => {
    if (cachedMarketId && cachedMarketId !== market?.id) {
      dispatch(actionGetMarketRequest({ id: cachedMarketId }));
    }

    dispatch(actionSetSearchOverlayVisible(false));
  }, [cachedMarketId, dispatch, market?.id]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => (document.body.style.overflow = 'unset');
  }, []);

  useEffect(() => {
    if (!isMounted && market?.id != null) {
      dispatch(actionCacheMarketId(market.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedChangeHandler = useMemo(
    () => debounce((input) => fetchQuery(input), DEBOUNCE_MILLISECONDS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getInputPropsClone = () => ({
    ...getInputProps(),
    onKeyDown: debouncedChangeHandler,
  });

  const fetchQuery = (e) => {
    const keyword = e.target.value;
    if (keyword.length >= SEARCH_TRACK_MAX_LENGTH) {
      trackSiteSearch(
        keyword,
        `Search revamp - not submitted user input - Market ID: ${market?.id}`
      );
    }
  };

  return (
    <FocusTrap active={overlayVisible}>
      <Box className={clsx(classes.root)}>
        <NavBarClose
          onClose={closeOverlay}
          variant={isMobile ? 'compact' : 'default'}
          classes={{ root: classes.navbar }}
        />
        <Box className={classes.content}>
          <OverlaySearchBar
            {...{
              getRootProps,
              getInputProps: getInputPropsClone,
              getClearProps,
              getListboxProps,
              getOptionProps,
              groupedOptions,
            }}
          />
          {inputProps.value.length === 0 && (
            <Box className={classes.popularSearchContainer}>
              <PopularSearches />
            </Box>
          )}
          <div className={classes.spacer} />
          <div className={classes.searchButtonContainer}>
            <Button
              onClick={() => {
                handleSearchSelect(inputProps.value, dispatch, true);
                goToSearchPage(history);
              }}>
              {SEARCH_ACTION_BUTTON_TEXT}
            </Button>
          </div>
        </Box>
      </Box>
    </FocusTrap>
  );
};

export default SearchOverlay;
